/**
 * 비밀번호 재설정 요청 — 사용자 ID + 등록된 이메일 입력 → 메일 발송.
 *
 * 보안상 사용자 존재 여부를 노출하지 않으므로, 입력값과 관계없이
 * 동일한 안내 화면을 표시 (성공처럼 보임).
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiCallError } from '../lib/api';
import * as authApi from '../lib/auth-api';

export function PasswordResetRequestPage() {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await authApi.requestPasswordReset({ userId, email });
      setDone(true);
    } catch (e) {
      const msg = e instanceof ApiCallError ? e.message : '요청 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
          <div className="text-3xl mb-2">📧</div>
          <h1 className="text-lg font-bold mb-2">메일을 확인하세요</h1>
          <p className="text-sm text-slate-600 mb-4">
            입력하신 ID 와 이메일이 일치하는 계정이 있다면<br />
            비밀번호 재설정 링크를 보냈습니다.
          </p>
          <p className="text-xs text-slate-500 mb-6">
            메일이 도착하지 않으면 스팸함을 확인하시거나<br />
            관리자에게 문의해주세요. (링크는 15분 후 만료됩니다)
          </p>
          <Link to="/login" className="text-sm text-blue-600 hover:underline">
            로그인 화면으로
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8"
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold">비밀번호 재설정</h1>
          <p className="mt-1 text-xs text-slate-500">
            등록된 회사 이메일로 재설정 링크를 보내드립니다
          </p>
        </div>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">아이디</span>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            autoComplete="username"
            // 폰이 첫 글자를 대문자로 바꾸지 않게 (v2.232.0 · 점검 ③ F-S3 · 서버도 소문자로 맞춘다)
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            required
            className="ui-input mt-1 w-full"
          />
        </label>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">등록된 회사 이메일</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            placeholder="example@smstm.co.kr"
            className="ui-input mt-1 w-full"
          />
        </label>

        {error && (
          <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full"
        >
          {submitting ? '발송 중…' : '재설정 링크 보내기'}
        </button>

        <div className="mt-4 text-center">
          <Link to="/login" className="text-xs text-slate-500 hover:underline">
            ← 로그인 화면으로
          </Link>
        </div>
      </form>
    </div>
  );
}
